import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Paper, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { tpms, alertLabel } from './api';
import { formatDateTime } from '../common/util/datetime';
import DownloadTable from '../shell/DownloadTable';

// Everything currently wrong, worst first, and a way to say it has been seen.
//
// Acknowledging is not closing: the alert stays open until the tyre actually recovers, judged by the
// same engine that raised it. Somebody saying "I know" must not make a flat tyre look mended.
const TpmsAlertsPage = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);
  const [history, setHistory] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(0);

  const load = (withHistory) => tpms(`/api/tpms/alerts${withHistory ? '?history=true' : ''}`)
    .then((rows) => { setAlerts(rows); setError(''); })
    .catch((e) => setError(e.message));

  // Watched, not loaded once. The dashboard refreshes every twenty seconds and the shell every
  // thirty; a list that only loaded when it was opened showed a recovered tyre as critical and never
  // showed a new one - so the badge, the health summary and the list disagreed about the same tyre.
  useEffect(() => {
    load(history);
    const timer = setInterval(() => load(history), 20000);
    return () => clearInterval(timer);
  }, [history]);

  const acknowledge = async (id) => {
    setBusy(id);
    try { await tpms(`/api/tpms/alerts/${id}/acknowledge`, 'POST'); await load(history); } catch (e) { setError(e.message); } finally { setBusy(0); }
  };

  if (!alerts && !error) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Tyre alerts</Typography>
          <Typography color="text.secondary">Raised by the monitoring rules, and closed when the tyre recovers.</Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={history} onChange={(e) => setHistory(e.target.checked)} />}
          label="Include closed"
        />
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      {!alerts?.length && !error && <Alert severity="success" sx={{ mt: 2 }}>No tyre alerts.</Alert>}

      {!!alerts?.length && (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <DownloadTable
              name="tyre-alerts"
              rows={alerts}
              columns={[
                ['Vehicle', (row) => row.vehicle],
                ['Tyre', (row) => row.position],
                ['Condition', (row) => alertLabel(row.type)],
                ['Severity', (row) => (row.severity === 'CRITICAL' ? 'Critical' : 'Warning')],
                ['State', (row) => (row.closedAt ? 'Recovered' : row.state.toLowerCase())],
                ['Since', (row) => formatDateTime(row.detectedAt)],
                ['Recovered at', (row) => (row.closedAt ? formatDateTime(row.closedAt) : '')],
              ]}
            />
          </Stack>
          <Paper variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Vehicle', 'Tyre', 'Condition', 'Severity', 'State', 'Since', ''].map((c, i) => (
                    <TableCell key={c || `a${i}`} align={c === '' ? 'right' : 'left'}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/tpms/vehicles/${row.vehicleId}`)} sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                        {row.vehicle || row.vehicleId}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.position}</TableCell>
                    <TableCell>{alertLabel(row.type)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.severity === 'CRITICAL' ? 'Critical' : 'Warning'} color={row.severity === 'CRITICAL' ? 'error' : 'warning'} />
                    </TableCell>
                    <TableCell>{row.closedAt ? 'Recovered' : row.state.toLowerCase()}</TableCell>
                    <TableCell>{formatDateTime(row.detectedAt)}</TableCell>
                    <TableCell align="right">
                      {!row.closedAt && row.state !== 'ACKNOWLEDGED' && (
                        <Button size="small" disabled={busy === row.id} onClick={() => acknowledge(row.id)}>Acknowledge</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TpmsAlertsPage;
